/**
 * Constantes globais da aplicação PWA Professores
 */

import { environment } from '@environments/environment';

export const API_ENDPOINTS = {
  BASE_URL: environment.apiUrl,
  FREQUENCIA: {
    ENTURMACOES: '/frequencia/turma/{turmaId}/frequencias/enturmacoes',
    FALTA_DIA: '/frequencia/falta-dia',
    FALTA_DIA_ENTURMACOES: '/frequencia/falta-dia/enturmacoes',
    FREQUENCIA_DIA_LOTE: '/frequencia/frequencia-dia/lote',
    CONFIGURACAO_FREQUENCIA: '/frequencia/frequencia-aula/configuracao-frequencia/{turmaId}',
    DIAS_MES: '/frequencia/falta-dia/dias-mes',
    PERIODO: '/frequencia/falta-dia/turma/{turmaId}/periodo/{periodoId}',
    RESULTADOS: '/frequencia/turma/{turmaId}/frequencias/resultados',
    PUBLICADOS: '/frequencia/turma/{turmaId}/frequencias/publicados'
  },
  COMMONS: {
    PERIODOS_AVALIATIVOS: '/commons/periodos-avaliativos',
    FREQUENCIA_DIA_DIAS_SEMANA: '/commons/frequencia-dia/dias-semana'
  },
  CONTEXT: {
    TURMA: '/context/turma'
  }
} as const;

export const TIPO_FREQUENCIA = {
  PRESENTE: 'PRESENTE',
  FALTA: 'FALTA',
  DISPENSADO: 'DISPENSADO',
  PRESENCA_REMOTA: 'PRESENCA_REMOTA'
} as const;

export type TipoFrequencia = typeof TIPO_FREQUENCIA[keyof typeof TIPO_FREQUENCIA];

export const STATUS_PRESENCA = {
  PRESENTE: 'presente',
  REMOTA: 'remota',
  FALTA: 'falta',
  DISPENSADO: 'dispensado'
} as const;

export type StatusPresenca = typeof STATUS_PRESENCA[keyof typeof STATUS_PRESENCA];

export const DIAS_SEMANA = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'] as const;

export const MESES_ANO = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
] as const;

export const STORAGE_KEYS = {
  BRIDGE_TOKEN: '__pwa_token',
  BRIDGE_APP_CONTEXT: '__pwa_app_context',
  BRIDGE_USER_ACCESS: '__pwa_user_access',
  /** Contexto completo do acesso atual (inclui admin) para fallback standalone. */
  BRIDGE_USER_ACCESS_CONTEXT: '__pwa_user_access_context',
  /** Par licenseId + id do documento de acesso (Mongo) para GET /api/access/{id}/permissions */
  BRIDGE_ACCESS_PAIR: '__pwa_access_pair',
  BRIDGE_CONTEXTO_COMPLETO: '__pwa_contexto_completo',
  ACCESS_TOKEN: 'access_token',
  AUTHENTICATION_DATA: 'authenticationData',
  PROFESSORES_CONTEXTO: 'professores.contexto'
} as const;

/**
 * Nomes antigos das chaves `__pwa_bridge_*` (pré rename). Fallback de leitura só por 1
 * release, pra sessão ativa no deploy não perder token/contexto. Remover após estabilizar.
 */
export const LEGACY_STORAGE_KEYS = {
  BRIDGE_TOKEN: '__pwa_bridge_token',
  BRIDGE_APP_CONTEXT: '__pwa_bridge_app_context',
  BRIDGE_USER_ACCESS: '__pwa_bridge_user_access',
  BRIDGE_USER_ACCESS_CONTEXT: '__pwa_bridge_user_access_context',
  BRIDGE_ACCESS_PAIR: '__pwa_bridge_access_pair',
  BRIDGE_CONTEXTO_COMPLETO: '__pwa_bridge_contexto_completo'
} as const;

export const USER_PROFILES = {
  ADMIN: 'ADMIN',
  PROFESSOR: 'PROFESSOR',
  SECRETARIO: 'SECRETARIO'
} as const;

export type UserProfile = typeof USER_PROFILES[keyof typeof USER_PROFILES];

export const ERROR_MESSAGES = {
  TOKEN_NAO_ENCONTRADO: 'Você precisa acessar primeiro a aplicação principal (http://localhost:9001) e fazer login antes de usar o PWA.',
  CONTEXTO_INVALIDO: 'Nenhum contexto selecionado. Por favor, selecione uma turma.',
  TURMA_NAO_SELECIONADA: 'Nenhuma turma selecionada. Por favor, selecione uma turma.',
  PERIODO_INVALIDO: 'Período avaliativo inválido.',
  CALENDARIO_INVALIDO: 'Erro ao inicializar calendário. Mês ou ano inválido.',
  ALUNOS_NAO_ENCONTRADOS: 'Nenhum aluno encontrado para esta turma.',
  PERMISSAO_NEGADA: 'Você não tem permissão para acessar esta turma. Entre em contato com a secretaria escolar.',
  PERMISSAO_NEGADA_FREQUENCIA: 'Você não tem permissão para acessar a frequência desta turma. Verifique com a secretaria escolar.',
  ERRO_SERVIDOR_PERIODOS: 'Erro no servidor ao carregar períodos avaliativos',
  ERRO_CARREGAR_PERIODOS: 'Erro ao carregar períodos avaliativos',
  ERRO_CARREGAR_FREQUENCIA: 'Erro ao carregar frequência. Verifique sua conexão e autenticação.',
  ERRO_SALVAR_FREQUENCIA: 'Erro ao salvar frequência',
  DIA_NAO_SELECIONADO: 'Selecione um dia no calendário para registrar a frequência.',
  CONTEXTO_INVALIDO_SALVAR: 'Contexto inválido. Selecione uma turma e período.',
  NENHUMA_ALTERACAO: 'Nenhuma alteração foi detectada.',
  AULA_NAO_REALIZADA: 'Selecione um dia no calendário primeiro.',
  ERRO_GERAL: 'Erro desconhecido'
} as const;

export const APP_CONFIG = {
  DEBOUNCE_TIME: 100,
  MAX_ERROS_DISPLAY: 5,
  TIMEOUT_REQUEST: 30000,
  TOKEN_WAIT_MAX_ATTEMPTS: 30,
  TOKEN_WAIT_DELAY_MS: 100,
  TOKEN_RENEWAL_INTERVAL_MS: 30000,
  TOKEN_MONITOR_INTERVAL_MS: 500,
  TOKEN_CHECK_INTERVAL_MS: 500,
  MODAL_COOLDOWN_MS: 30000,
  INSTALL_PROMPT_DELAY_MS: 10000,
  INSTALL_DISMISS_DURATION_MS: 7 * 24 * 60 * 60 * 1000 // 7 dias
} as const;